import React, { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  Activity, ChevronDown, ChevronUp, CheckCircle2, AlertTriangle,
  ArrowRight, Info, TrendingUp, CircleAlert
} from "lucide-react";

const API = process.env.REACT_APP_API || "";

const colorMap = {
  green: { ring: "stroke-green-500", text: "text-green-700", bg: "bg-green-50", dot: "bg-green-500" },
  yellow: { ring: "stroke-yellow-500", text: "text-yellow-700", bg: "bg-yellow-50", dot: "bg-yellow-500" },
  orange: { ring: "stroke-orange-500", text: "text-orange-700", bg: "bg-orange-50", dot: "bg-orange-500" },
  red: { ring: "stroke-red-500", text: "text-red-700", bg: "bg-red-50", dot: "bg-red-500" },
  gray: { ring: "stroke-gray-300", text: "text-gray-500", bg: "bg-gray-50", dot: "bg-gray-400" },
};

function ScoreRing({ score, color, size = 104 }) {
  const c = colorMap[color] || colorMap.gray;
  if (score === null || score === undefined) {
    return <div style={{ width: size, height: size }} className="rounded-full border-8 border-gray-200 flex items-center justify-center text-xs text-gray-400 text-center p-2">Noch keine Daten</div>;
  }
  const r = 43;
  const circumference = 2 * Math.PI * r;
  const dash = (score / 100) * circumference;
  return (
    <div className="relative" style={{ width: size, height: size }}>
      <svg width={size} height={size} viewBox="0 0 104 104" className="-rotate-90">
        <circle cx="52" cy="52" r={r} fill="none" stroke="#E5E7EB" strokeWidth="9" />
        <circle cx="52" cy="52" r={r} fill="none" strokeWidth="9" strokeLinecap="round" className={c.ring}
          strokeDasharray={`${dash} ${circumference - dash}`} />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <span className={`text-2xl font-bold ${c.text}`}>{score}</span>
        <span className="text-[10px] text-gray-400">von 100</span>
      </div>
    </div>
  );
}

function SectionRow({ section, setTab }) {
  const c = colorMap[section.color] || colorMap.gray;
  return (
    <div className="flex items-center gap-3 py-2 border-b last:border-0">
      <span className={`w-2.5 h-2.5 rounded-full ${c.dot}`} />
      <div className="flex-1 min-w-0"><div className="text-sm font-medium">{section.title}</div><div className="text-xs text-gray-500">{section.available ? section.label : "Noch nicht genügend Daten"}</div></div>
      <div className={`font-semibold ${c.text}`}>{section.available ? section.score : "–"}</div>
      {section.available && section.warnings?.[0]?.tab && (
        <button onClick={() => setTab?.(section.warnings[0].tab)} className="text-xs text-[var(--brand-primary)] hover:underline">Details</button>
      )}
    </div>
  );
}

export function HealthScoreCard({ setTab, compact = false, showActions = true }) {
  const [data, setData] = useState(null);
  const [history, setHistory] = useState([]);
  const [openMethod, setOpenMethod] = useState(false);
  const [loading, setLoading] = useState(true);

  const headers = useCallback(() => {
    const token = localStorage.getItem("token") || sessionStorage.getItem("token");
    return { Authorization: `Bearer ${token}` };
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [score, trend] = await Promise.all([
        axios.get(`${API}/api/health/score`, { headers: headers() }),
        axios.get(`${API}/api/health/history?months=6`, { headers: headers() }),
      ]);
      setData(score.data);
      setHistory(trend.data.history || []);
    } catch (err) {
      console.error("Health Score konnte nicht geladen werden", err);
    } finally { setLoading(false); }
  }, [headers]);

  useEffect(() => { load(); }, [load]);

  if (loading) return <Card className="p-5 border-[var(--brand-border)]"><span className="text-sm text-gray-400">Business Health Score wird berechnet…</span></Card>;
  if (!data) return null;
  const c = colorMap[data.color] || colorMap.gray;

  return (
    <Card className="p-5 border-[var(--brand-border)] space-y-4" data-testid="health-score-card">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="flex items-center gap-2"><Activity className="w-5 h-5 text-[var(--brand-primary)]" /><div><h3 className="font-semibold text-[var(--brand-ink)]">Business Health Score</h3><p className="text-xs text-gray-500">Operativer Hinweis auf Basis deiner vorhandenen Daten</p></div></div>
        <span className="text-xs text-gray-400">{data.data_sections} von {data.total_sections} Bereichen auswertbar</span>
      </div>

      <div className={`flex flex-col sm:flex-row gap-5 ${compact ? "items-center" : "items-start"}`}>
        <div className="flex flex-col items-center gap-1"><ScoreRing score={data.score} color={data.color} /><span className={`text-sm font-semibold ${c.text}`}>{data.label}</span></div>
        <div className="flex-1 w-full">
          {data.score === null ? (
            <div className="text-sm text-gray-500 bg-gray-50 rounded-md p-3">Für einen Score liegen noch nicht genügend Daten vor. Sobald du Daten in deinen gebuchten Studios erfasst, werden nur die verfügbaren Bereiche berücksichtigt.</div>
          ) : (
            <>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-x-5">
                {data.sections.map((section) => <SectionRow key={section.id} section={section} setTab={setTab} />)}
              </div>
              {history.length > 1 && (
                <div className="mt-3 pt-3 border-t"><div className="text-xs font-medium text-gray-500 mb-2">Verlauf</div><div className="flex gap-2 overflow-x-auto">{history.slice(-6).map((point) => <div key={point.date} className="text-center min-w-12"><div className="text-sm font-semibold">{point.score}</div><div className="text-[10px] text-gray-400">{point.date.slice(5)}</div></div>)}</div></div>
              )}
            </>
          )}
        </div>
      </div>

      {!compact && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div className="bg-green-50 rounded-md p-3"><div className="text-xs font-semibold text-green-700 mb-1 flex items-center gap-1"><CheckCircle2 className="w-3.5 h-3.5" /> Positiv</div>{data.positives.length ? data.positives.map((text, i) => <div key={i} className="text-xs text-green-800">• {text}</div>) : <div className="text-xs text-green-800">Noch keine besonderen positiven Hinweise.</div>}</div>
          <div className="bg-orange-50 rounded-md p-3"><div className="text-xs font-semibold text-orange-700 mb-1 flex items-center gap-1"><AlertTriangle className="w-3.5 h-3.5" /> Aufmerksamkeit erforderlich</div>{data.attention.length ? data.attention.map((text, i) => <div key={i} className="text-xs text-orange-800">• {text}</div>) : <div className="text-xs text-orange-800">Keine kritischen Hinweise erkannt.</div>}</div>
        </div>
      )}

      {showActions && data.next_actions?.length > 0 && (
        <div className="pt-1"><div className="flex items-center gap-2 mb-2"><TrendingUp className="w-4 h-4 text-[var(--brand-primary)]" /><h4 className="font-semibold text-sm">Das solltest du heute zuerst erledigen</h4></div><div className="space-y-2">{data.next_actions.map((action, index) => <div key={action.id || index} className="flex items-center gap-3 p-3 bg-[var(--brand-bg)] rounded-md"><span className="w-6 h-6 rounded-full bg-[var(--brand-primary)] text-white text-xs flex items-center justify-center shrink-0">{index + 1}</span><div className="flex-1 min-w-0"><div className="text-sm font-medium">{action.title}</div><div className="text-xs text-gray-500">{action.description}</div></div><Button size="sm" variant="outline" onClick={() => setTab?.(action.tab)} className="shrink-0">{action.action === "payment_reminder" ? "Rechnung öffnen" : "Öffnen"}<ArrowRight className="w-3.5 h-3.5 ml-1" /></Button></div>)}</div></div>
      )}

      <div className="border-t pt-3"><button onClick={() => setOpenMethod(!openMethod)} className="text-xs text-[var(--brand-primary)] flex items-center gap-1 hover:underline"><Info className="w-3.5 h-3.5" />Wie wird mein Score berechnet? {openMethod ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}</button>{openMethod && <div className="mt-2 bg-gray-50 rounded-md p-3 space-y-2"><p className="text-xs text-gray-600">{data.methodology.disclaimer}</p>{data.methodology.rules.map((rule) => <div key={rule.section} className="text-xs"><span className="font-semibold">{rule.section}: </span><span className="text-gray-600">{rule.method}</span></div>)}</div>}</div>
    </Card>
  );
}
